import { useEffect, useRef } from 'react';
import type * as Y from 'yjs';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';

const DEBOUNCE_MS = 1500;

/** Wrap a PNG (or any image) data-URL as an HTML `<img>` preview markup string. */
export function buildCanvasPreviewMarkup(dataUrl: string): string {
  if (!dataUrl) return '';
  return `<img src="${dataUrl}" alt="" style="width:100%;height:auto;display:block" />`;
}

interface UseCanvasPreviewMarkupOptions {
  /** Entity id of the canvas whose card preview this updates. */
  documentId: string;
  /** Gate — only emit when the user can edit (false for read-only viewers). */
  enabled: boolean;
  /**
   * Produce a snapshot of the current canvas scene as an image data-URL (PNG).
   * Injected so the hook's debounce/dedup logic is testable without the real
   * html-to-image + React Flow DOM capture. Returns '' when there's nothing to
   * snapshot. See {@link useCanvasSceneSnapshot} for the production impl.
   */
  snapshot: () => Promise<string>;
}

/**
 * Keeps a canvas's card preview markup in sync with its content. On each canvas
 * change (nodes/edges, debounced 1.5s), snapshot the rendered scene to a PNG
 * data-URL, wrap it as `<img>` HTML, and ship it via the workspace service.
 * This is the canvas counterpart of the document `usePreviewMarkup` hook —
 * canvas page cards render the stored preview markup (kind 'html'), which was
 * previously produced by Excalidraw and lost when whiteboard was removed.
 *
 * The snapshot is injected (DI) because capturing the React Flow viewport needs
 * the live DOM; the production wiring lives in {@link useCanvasSceneSnapshot}.
 */
export function useCanvasPreviewMarkup(
  doc: Y.Doc | null,
  { documentId, enabled, snapshot }: UseCanvasPreviewMarkupOptions,
): void {
  const service = useWorkspaceService();
  const binding = useCanvasBinding();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!doc || !enabled) return;

    const flush = () => {
      void snapshot().then((dataUrl) => {
        const markup = buildCanvasPreviewMarkup(dataUrl);
        if (!markup) return; // nothing to preview
        if (lastSentRef.current === markup) return; // dedup
        lastSentRef.current = markup;
        service.updatePreviewMarkup(documentId, markup, 'html');
      });
    };

    const onChange = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, DEBOUNCE_MS);
    };

    const { nodes, edges } = canvasRoots(doc, binding);
    nodes.observeDeep(onChange);
    edges.observeDeep(onChange);
    return () => {
      nodes.unobserveDeep(onChange);
      edges.unobserveDeep(onChange);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [doc, enabled, documentId, service, snapshot, binding]);
}
